import React, { useState, useEffect, useMemo } from 'react';
import { apiClient } from '../utils/apiClient';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useProfile } from '../contexts/ProfileContext';
import DocumentationViewer from '../components/DocumentationViewer';
import FilterSidebar from '../components/FilterSidebar';

const Workflows = () => {
  const [workflows, setWorkflows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [showDocumentation, setShowDocumentation] = useState(false);
  const [hoveredCardId, setHoveredCardId] = useState(null);
  
  // Filter and sort state
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [sortBy, setSortBy] = useState('name');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  
  const { addWorkflowToCart, isInCart } = useCart();
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const { addWorkflowToProfile, isWorkflowInActiveProfile, refreshProfile } = useProfile();

  useEffect(() => {
    fetchWorkflows();
    // Refresh profile data to ensure we have the latest state
    refreshProfile();
  }, [refreshProfile]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSortDropdown && !event.target.closest('[data-dropdown]')) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSortDropdown]);

  // Get unique categories and statuses from workflows
  const categories = useMemo(() => {
    const categoryMap = {};
    workflows.forEach(workflow => {
      if (workflow.category) {
        categoryMap[workflow.category] = (categoryMap[workflow.category] || 0) + 1;
      }
    });
    
    // Add "All" option at the beginning
    const allCategories = [
      { value: 'ALL', label: 'All', count: workflows.length }
    ];
    
    // Add specific categories
    Object.entries(categoryMap).forEach(([category, count]) => {
      allCategories.push({
        value: category,
        label: category,
        count
      });
    });
    
    return allCategories;
  }, [workflows]);

  const statuses = useMemo(() => {
    const statusMap = {};
    workflows.forEach(workflow => {
      statusMap[workflow.status] = (statusMap[workflow.status] || 0) + 1;
    });
    
    // Ensure both AVAILABLE and UNAVAILABLE are always present
    const allStatuses = [
      { value: 'AVAILABLE', label: 'Available', count: statusMap['AVAILABLE'] || 0 },
      { value: 'UNAVAILABLE', label: 'Unavailable', count: statusMap['UNAVAILABLE'] || 0 }
    ];
    
    return allStatuses;
  }, [workflows]);

  // Comprehensive filtering and sorting for workflows
  const filteredWorkflows = useMemo(() => {
    if (!workflows) return [];
    
    let filtered = [...workflows];

    // Search filter - only search by name and description
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(workflow =>
        workflow.name.toLowerCase().includes(term) ||
        (workflow.description && workflow.description.toLowerCase().includes(term))
      );
    }

    // Category filter
    if (selectedCategories.length > 0 && !selectedCategories.includes('ALL')) {
      filtered = filtered.filter(workflow => 
        workflow.category && selectedCategories.includes(workflow.category)
      );
    }

    // Status filter
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(workflow => 
        selectedStatuses.includes(workflow.status)
      );
    }

    // Sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'status':
          return a.status.localeCompare(b.status);
        case 'category':
          return (a.category || '').localeCompare(b.category || '');
        case 'created':
          return new Date(b.createdAt) - new Date(a.createdAt);
        default:
          return 0;
      }
    });

    return filtered;
  }, [workflows, searchTerm, selectedCategories, selectedStatuses, sortBy]);

  const fetchWorkflows = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/workflows');
      setWorkflows(response.data.data);
    } catch (err) {
      setError('Failed to fetch workflows');
      console.error('Error fetching workflows:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (workflowId) => {
    if (!user) {
      alert('Please log in to add items to cart');
      return;
    }

    const result = await addWorkflowToCart(workflowId);
    if (!result.success) {
      alert(result.error);
    }
  };

  const handleAddToProfile = async (workflowId) => {
    if (!user) {
      alert('Please log in to add items to profile');
      return;
    }

    // Check if workflow is already in active profile
    if (isWorkflowInActiveProfile(workflowId)) {
      console.log('Workflow already in active profile, ignoring request');
      return; // Do nothing if already in profile
    }

    try {
      const result = await addWorkflowToProfile(workflowId);
      if (!result.success) {
        alert(result.error);
      }
    } catch (error) {
      console.error('Error adding to profile:', error);
      alert('Failed to add to profile');
    }
  };

  // Filter and sort handlers
  const handleCategoryChange = (category) => {
    setSelectedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const handleStatusChange = (status) => {
    setSelectedStatuses(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const handleClearFilters = () => {
    setSelectedCategories([]);
    setSelectedStatuses([]);
    setSearchTerm('');
  };

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
    setShowSortDropdown(false);
  };

  const sortOptions = [
    { value: 'name', label: 'Name' },
    { value: 'status', label: 'Status' },
    { value: 'category', label: 'Category' },
    { value: 'created', label: 'Date Created' }
  ];

  const getStatusBadge = (status) => {
    const statusColors = {
      AVAILABLE: '#28a745',
      UNAVAILABLE: '#6c757d'
    };
    
    return (
      <span
        style={{
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 'bold',
          color: 'white',
          backgroundColor: statusColors[status] || '#6c757d'
        }}
      >
        {status}
      </span>
    );
  };

  const getCategoryColor = (category) => {
    // Generate consistent colors based on category name
    const colors = [
      '#e74c3c', // Red
      '#3498db', // Blue
      '#2ecc71', // Green
      '#f39c12', // Orange
      '#9b59b6', // Purple
      '#1abc9c', // Turquoise
      '#e67e22', // Carrot
      '#34495e', // Dark blue-gray
      '#e91e63', // Pink
      '#00bcd4', // Cyan
      '#8bc34a', // Light green
      '#ff9800', // Amber
      '#795548', // Brown
      '#607d8b', // Blue-gray
      '#ff5722'  // Deep orange
    ];
    
    if (!category) return '#6c757d';
    
    // Simple hash function to get consistent color for same category
    let hash = 0;
    for (let i = 0; i < category.length; i++) {
      hash = category.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };


  if (loading) {
  return (
    <div className="dashboard-page" style={{ 
      background: isDarkMode ? 'linear-gradient(135deg, #282440 0%, #1a1a2e 100%)' : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      color: isDarkMode ? 'white' : '#333',
      paddingTop: '0'
    }}>
      <div className="container">
          <div className="flex-center" style={{ height: '50vh' }}>
            <div>Loading workflows...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page" style={{ 
      background: isDarkMode ? 'linear-gradient(135deg, #282440 0%, #1a1a2e 100%)' : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      color: isDarkMode ? 'white' : '#333',
      paddingTop: '0'
    }}>
      <div className="container">
        {/* Header Section with Logo and Title */}
        <div style={{ marginBottom: '3rem' }}>
          {/* Centered Title */}
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <h1 style={{ 
              fontSize: '3rem', 
              fontWeight: 'bold', 
              marginBottom: '1rem',
              color: '#00CC66'
            }}>
              Workflows
            </h1>
          </div>
          
          {/* Subheader text - centered on the page */}
          <div style={{ 
            marginBottom: '1.5rem'
          }}>
            <p style={{ 
              fontSize: 'clamp(1rem, 2.5vw, 1.2rem)', 
              color: isDarkMode ? '#B0B0B0' : '#666',
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: '1.6',
              textAlign: 'center',
              padding: '0 clamp(0.5rem, 2vw, 0)'
            }}>
              Streamline your bioinformatics analysis with pre-configured workflows that combine multiple MCP servers. 
              These ready-to-use pipelines accelerate your research and ensure reproducible results.
            </p>
          </div>
          
          {/* Workflow count badge */}
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              backgroundColor: isDarkMode ? 'rgba(0, 204, 102, 0.1)' : 'rgba(0, 204, 102, 0.1)',
              borderRadius: '20px',
              border: `1px solid ${isDarkMode ? 'rgba(0, 204, 102, 0.3)' : 'rgba(0, 204, 102, 0.3)'}`
            }}>
              <span style={{ 
                color: isDarkMode ? '#00CC66' : '#00CC66',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}>
                {filteredWorkflows.length} workflow{filteredWorkflows.length !== 1 ? 's' : ''} available
              </span>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger">
            {error}
          </div>
        )}

        {/* Main Content with Sidebar */}
        <div className="mcp-servers-layout">
          {/* Left Side: Filter Sidebar */}
          <div className="filter-sidebar-wrapper">
            <FilterSidebar
              categories={categories}
              statuses={statuses}
              selectedCategories={selectedCategories}
              selectedStatuses={selectedStatuses}
              onCategoryChange={handleCategoryChange}
              onStatusChange={handleStatusChange}
              onClearFilters={handleClearFilters}
              title="Filters"
            />
          </div>

          {/* Right Side: Search, Sort, and Workflow List */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Search and Sort Controls */}
            <div className="search-sort-container" style={{
              backgroundColor: isDarkMode ? '#1a1a1a' : '#f8f9fa',
              border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`,
              borderRadius: '8px',
              padding: 'clamp(1rem, 3vw, 1.5rem)',
              marginBottom: '1.5rem'
            }}>
              {/* Search Element */}
              <div className="search-wrapper" style={{ 
                width: '300px', 
                position: 'relative'
              }}>
                <div style={{
                  position: 'absolute',
                  left: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#007bff',
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search by name or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem 0.75rem 2.5rem',
                    border: `1px solid ${isDarkMode ? '#555' : '#ddd'}`,
                    borderRadius: '8px',
                    backgroundColor: isDarkMode ? '#2a2a2a' : 'white',
                    color: isDarkMode ? 'white' : '#333',
                    fontSize: '0.9rem',
                    outline: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#007bff';
                    e.target.style.boxShadow = '0 0 0 2px rgba(0, 123, 255, 0.25)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = isDarkMode ? '#555' : '#ddd';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Sort Element - Anchored to the right */}
              <div className="sort-wrapper" style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem', 
                marginLeft: 'auto'
              }}>
                <span style={{
                  color: isDarkMode ? '#aaa' : '#666',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}>
                  Showing {filteredWorkflows.length} of {workflows.length}
                </span>
                
                <div style={{ position: 'relative' }} data-dropdown>
                  <button
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: isDarkMode ? '#2a2a2a' : 'white',
                      border: `1px solid ${isDarkMode ? '#555' : '#ddd'}`,
                      borderRadius: '6px',
                      color: isDarkMode ? 'white' : '#333',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                    onClick={() => setShowSortDropdown(!showSortDropdown)}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = isDarkMode ? '#3a3a3a' : '#f8f9fa';
                      e.target.style.borderColor = '#007bff';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = isDarkMode ? '#2a2a2a' : 'white';
                      e.target.style.borderColor = isDarkMode ? '#555' : '#ddd';
                    }}
                  >
                    Sort by {sortOptions.find(opt => opt.value === sortBy)?.label || 'popularity'}
                    <span>▼</span>
                  </button>
                  
                  {/* Dropdown Menu */}
                  {showSortDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      backgroundColor: isDarkMode ? '#2a2a2a' : 'white',
                      border: `1px solid ${isDarkMode ? '#555' : '#ddd'}`,
                      borderRadius: '6px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      zIndex: 1000,
                      minWidth: '150px',
                      marginTop: '0.25rem'
                    }}>
                    {sortOptions.map((option) => (
                      <div
                        key={option.value}
                        style={{
                          padding: '0.75rem 1rem',
                          cursor: 'pointer',
                          color: isDarkMode ? 'white' : '#333',
                          fontSize: '0.9rem',
                          borderBottom: `1px solid ${isDarkMode ? '#333' : '#f0f0f0'}`,
                          transition: 'background-color 0.2s ease'
                        }}
                        onClick={() => handleSortChange(option.value)}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = isDarkMode ? '#3a3a3a' : '#f8f9fa';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                        }}
                      >
                        {option.label}
                      </div>
                    ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Workflow List */}
            {filteredWorkflows.length === 0 ? (
              <div className="card">
                <h3>{workflows.length === 0 ? 'No Workflows Found' : 'No Matching Workflows Found'}</h3>
                <p>{workflows.length === 0 ? 'There are currently no workflows configured.' : 'Try adjusting your search terms or filters.'}</p>
                {workflows.length > 0 && (
                  <button
                    className="btn btn-secondary"
                    onClick={handleClearFilters}
                    style={{ marginTop: '1rem' }}
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            ) : (
              <div className="mcp-servers-grid">
                {filteredWorkflows.map((workflow) => (
            <div 
              key={workflow.id} 
              className="card"
              style={{ 
                cursor: 'pointer',
                transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                borderColor: hoveredCardId === workflow.id ? '#28a745' : (isDarkMode ? '#555' : '#ddd'),
                borderWidth: '1px',
                borderStyle: 'solid',
                boxShadow: hoveredCardId === workflow.id ? '0 4px 12px rgba(40, 167, 69, 0.4), 0 0 0 1px rgba(40, 167, 69, 0.3)' : 'none',
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
              }}
              onClick={() => {
                if (workflow.documentation) {
                  setSelectedWorkflow(workflow);
                  setShowDocumentation(true);
                }
              }}
              onMouseEnter={() => setHoveredCardId(workflow.id)}
              onMouseLeave={() => setHoveredCardId(null)}
            >
              {/* Top row: Category tag only */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                {workflow.category && (
                  <span style={{ 
                    display: 'inline-block',
                    backgroundColor: getCategoryColor(workflow.category),
                    color: 'white',
                    padding: '0.125rem 0.5rem',
                    borderRadius: '8px',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    whiteSpace: 'nowrap'
                  }}>
                    {workflow.category}
                  </span>
                )}
              </div>
              
              {/* Second row: Name spanning full width */}
              <h3 style={{ margin: 0, marginBottom: '0.5rem', width: '100%' }}>{workflow.name}</h3>
              
              {/* Description spanning full width - flexible */}
              <p style={{ 
                color: isDarkMode ? '#e0e0e0' : '#333', 
                marginBottom: '0.5rem', 
                width: '100%',
                flex: 1
              }}>
                {workflow.description || 'No description provided'}
              </p>
              
              {/* Action Buttons - anchored to bottom */}
              <div 
                style={{ 
                  marginTop: 'auto',
                  paddingTop: '0.5rem', 
                  borderTop: `1px solid ${isDarkMode ? '#555' : '#eee'}`,
                  display: 'flex',
                  gap: '0.5rem',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'border-color 0.3s ease'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Status Badge - Bottom Left */}
                <div>
                  {getStatusBadge(workflow.status)}
                </div>
                
                {/* Action Buttons - Bottom Right */}
                {user && (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={() => handleAddToCart(workflow.id)}
                      disabled={isInCart('workflow', workflow.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '48px',
                        height: '48px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: isInCart('workflow', workflow.id) ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease',
                        color: isInCart('workflow', workflow.id) ? '#6c757d' : (isDarkMode ? '#e0e0e0' : '#333'),
                        opacity: isInCart('workflow', workflow.id) ? 0.6 : 1
                      }}
                      title={isInCart('workflow', workflow.id) ? 'In Cart' : 'Add to Cart'}
                      onMouseEnter={(e) => {
                        if (!isInCart('workflow', workflow.id)) {
                          e.target.style.backgroundColor = isDarkMode ? '#4a4a4a' : '#f8f9fa';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                      }}
                    >
                      <div style={{
                        width: '28px',
                        height: '28px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '3px'
                      }}>
                        <span style={{ fontSize: '16px', fontWeight: 'bold' }}>+</span>
                        {/* Cart icon */}
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M7 18c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12L8.1 13h7.45c.75 0 1.41-.41 1.75-1.03L21.7 4H5.21l-.94-2H1zm16 16c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                        </svg>
                      </div>
                    </button>
                    <button
                      className="mcp-profile-button"
                      onClick={() => {
                        console.log(`Workflows - Profile button clicked for ${workflow.name} (${workflow.id})`);
                        console.log(`Workflows - isWorkflowInActiveProfile(${workflow.id}):`, isWorkflowInActiveProfile(workflow.id));
                        if (!isWorkflowInActiveProfile(workflow.id)) {
                          handleAddToProfile(workflow.id);
                        } else {
                          console.log('Workflows - Workflow already in profile, ignoring click');
                        }
                      }}
                      disabled={isWorkflowInActiveProfile(workflow.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '48px',
                        height: '48px',
                        backgroundColor: 'transparent !important',
                        border: 'none !important',
                        borderRadius: '8px',
                        cursor: isWorkflowInActiveProfile(workflow.id) ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease',
                        color: isWorkflowInActiveProfile(workflow.id) ? '#6c757d' : (isDarkMode ? '#e0e0e0' : '#333'),
                        opacity: isWorkflowInActiveProfile(workflow.id) ? 0.4 : 1,
                        pointerEvents: isWorkflowInActiveProfile(workflow.id) ? 'none' : 'auto'
                      }}
                      title={isWorkflowInActiveProfile(workflow.id) ? 'Already in Profile' : 'Add to Profile'}
                      onMouseEnter={(e) => {
                        if (!isWorkflowInActiveProfile(workflow.id)) {
                          e.target.style.backgroundColor = isDarkMode ? '#4a4a4a' : '#f8f9fa';
                        } else {
                          e.target.style.backgroundColor = 'transparent';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                      }}
                    >
                      <div style={{
                        width: '28px',
                        height: '28px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '3px'
                      }}>
                        <span style={{ fontSize: '16px', fontWeight: 'bold' }}>+</span>
                        {/* Profile icon */}
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z"/>
                        </svg>
                      </div>
                    </button>
                  </div>
                )}
              </div>
              
            </div>
          ))}
              </div>
            )}
          </div>
        </div>

      {/* Documentation Viewer Modal */}
      {showDocumentation && selectedWorkflow && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 'clamp(1rem, 3vw, 2rem)'
        }}>
          <div style={{
            backgroundColor: isDarkMode ? '#2a2a2a' : 'white',
            borderRadius: '8px',
            maxWidth: 'min(800px, 95vw)',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative',
            margin: '1rem'
          }}>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setShowDocumentation(false);
                setSelectedWorkflow(null);
              }}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                zIndex: 1001
              }}
            >
              ×
            </button>
            <DocumentationViewer
              documentation={selectedWorkflow.documentation}
              title={selectedWorkflow.name}
              iconUrl={selectedWorkflow.iconUrl}
              iconAlt={selectedWorkflow.iconAlt}
              category={selectedWorkflow.category}
              tags={selectedWorkflow.tags}
              lastDocumentedAt={selectedWorkflow.lastDocumentedAt}
              documentedBy={selectedWorkflow.documentedBy}
            />
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Workflows;
